import { useEffect, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const getReducedMotion = () =>
    Boolean(globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches);

export function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(getReducedMotion);

    useEffect(() => {
        const media = globalThis.matchMedia?.(REDUCED_MOTION_QUERY);
        if (!media) {
            return undefined;
        }
        const onChange = (event) => setReduced(event.matches);
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    return reduced;
}

export function useRevealOnScroll() {
    useEffect(() => {
        const targets = Array.from(document.querySelectorAll('[data-reveal]'));
        const reveal = (el) => el.classList.add('is-visible');

        if (!('IntersectionObserver' in globalThis) || getReducedMotion()) {
            targets.forEach(reveal);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        reveal(entry.target);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
        );
        targets.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);
}

export function useActiveSection(ids) {
    const [active, setActive] = useState(ids[0]);

    useEffect(() => {
        if (!('IntersectionObserver' in globalThis)) {
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                entries
                    .filter((entry) => entry.isIntersecting)
                    .forEach((entry) => setActive(entry.target.id));
            },
            { rootMargin: '-45% 0px -50% 0px' }
        );
        ids.map((id) => document.getElementById(id))
            .filter(Boolean)
            .forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [ids]);

    return active;
}

export function useScrollProgress() {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        let frame = 0;
        const update = () => {
            frame = 0;
            const { scrollHeight, clientHeight } = document.documentElement;
            const max = scrollHeight - clientHeight;
            setProgress(max > 0 ? globalThis.scrollY / max : 0);
        };
        const onScroll = () => {
            if (!frame) {
                frame = globalThis.requestAnimationFrame(update);
            }
        };
        update();
        globalThis.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            globalThis.removeEventListener('scroll', onScroll);
            if (frame) {
                globalThis.cancelAnimationFrame(frame);
            }
        };
    }, []);

    return progress;
}
